/**
 * Courses filtering: helpers for the community courses list surface.
 *
 * Companion files:
 *  - `types/courses-filtering.ts` — types (API contract, sort, grouping, composable return)
 *  - `constants/courses-filtering.ts` — value constants (sort modes, backend limits, tag display config)
 */

import type { Component } from 'vue'

import { Tag01Icon } from '@gohighlevel/ghl-icons/24/outline'

import {
  CATEGORY_ICON_MAP,
  COURSE_SORT_MODES,
  FILTER_CATEGORY_ORDER,
  MONGO_OBJECT_ID_REGEX,
  TAG_CATEGORY_ORDER,
  TAG_IDS_FILTER_MAX_SIZE,
} from '../constants/courses-filtering'
import type {
  CourseSortByValue,
  CourseSortMode,
  CourseTagCategory,
  GroupCourseTag,
  GroupedCourseTagCategory,
  SortDirectionValue,
} from '../types/courses-filtering'

// ============================================================================
// Sort
// ============================================================================

/** Resolve the sort mode matching an applied `{ sortBy, sort }` pair, falling back to the default. */
export const resolveCourseSortMode = (sortBy: CourseSortByValue, sort: SortDirectionValue): CourseSortMode =>
  COURSE_SORT_MODES.find(mode => mode.sortBy === sortBy && mode.sort === sort) ?? COURSE_SORT_MODES[0]

// ============================================================================
// Tag id validation / normalization
// ============================================================================

/** True when `value` is a well-formed 24-char hex ObjectId. */
export const isValidCourseTagId = (value: unknown): value is string => typeof value === 'string' && MONGO_OBJECT_ID_REGEX.test(value)

/**
 * Filter/normalize a raw candidate id list down to what the backend will accept.
 *
 * NOTE: The cap enforcement here is defense-in-depth. The primary UX-level guard lives in
 * `CoursesFilterSidebar.vue`, which disables unchecked checkboxes once the cap is reached and
 * surfaces an inline hint explaining why. This truncation branch should only trigger for
 * corrupt/hydrated state (e.g. a stale persisted selection); in normal flow it is a no-op.
 */
export const sanitizeCourseTagIds = (ids: readonly string[] | undefined | null): string[] => {
  if (!ids || ids.length === 0) return []
  const seen = new Set<string>()
  const clean: string[] = []
  for (const id of ids) {
    if (!isValidCourseTagId(id)) continue
    if (seen.has(id)) continue
    seen.add(id)
    clean.push(id)
    if (clean.length >= TAG_IDS_FILTER_MAX_SIZE) break
  }
  return clean
}

// ============================================================================
// Category grouping (sidebar)
// ============================================================================

/**
 * Group tags by category preserving first-seen order (stable across renders).
 * Consumers can render one collapsible section per returned entry.
 */
export const groupCourseTagsByCategory = (tags: readonly GroupCourseTag[]): GroupedCourseTagCategory[] => {
  const byCategory = new Map<string, GroupCourseTag[]>()
  for (const tag of tags) {
    const bucket = byCategory.get(tag.category)
    if (bucket) {
      bucket.push(tag)
    } else {
      byCategory.set(tag.category, [tag])
    }
  }
  return Array.from(byCategory.entries()).map(([category, entries]) => ({ category, tags: entries }))
}

// ============================================================================
// Category rank + icon helpers
// ============================================================================

/**
 * Build a rank function for a fixed category display order. The returned function is
 * case-insensitive and appends unknown / missing categories after the catalog so the caller's
 * stable sort naturally falls back to API order.
 */
const createCategoryRankFn =
  (order: readonly string[]) =>
  (category: string | undefined): number => {
    const key = category?.toLowerCase?.() ?? ''
    const index = order.indexOf(key)
    return index === -1 ? order.length : index
  }

export const getTagCategoryRank = createCategoryRankFn(TAG_CATEGORY_ORDER)

export const getFilterCategoryRank = createCategoryRankFn(FILTER_CATEGORY_ORDER)

export const getTagIcon = (category: string | undefined): Component => {
  const key = category?.toLowerCase?.() ?? ''
  if (key in CATEGORY_ICON_MAP) {
    return CATEGORY_ICON_MAP[key as CourseTagCategory]
  }
  return Tag01Icon
}
